import { useMemo, useRef, useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Grid2 as Grid,
    Menu,
    MenuItem,
    Button,
    LinearProgress,
    IconButton,
    CircularProgress,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { COLORS } from '../../../utils/Application_Constants';
import useToast from '../../../utils/useToast';
import { convertBase64ToBlob, downloadBlobFile } from '../../../utils/helpers';

const DOCUMENT_TYPES = [
    { value: 'FacilityTimesheet', label: 'Facility Timesheet' },
    { value: 'ClientConfirmation', label: 'Client Confirmation (CC)' },
    { value: 'ClientApproval', label: 'Client Approval' },
    { value: 'PaperTimesheet', label: 'Paper Timesheet' },
    { value: 'MileageAttachment', label: 'Mileage Attachment' },
];

const styles = () => ({
    container: {
        border: `1px solid ${COLORS.LT_MIDNIGHT10}`,
        padding: '11px 16px',
        borderRadius: '8px',
        position: 'relative',
    },
    fileIcon: {
        backgroundColor: COLORS.TRUE_BLUE,
        height: '44px',
        width: '42px',
        display: 'flex',
        borderRadius: '8px',
        border: `1px solid ${COLORS.LT_MIDNIGHT}`,
    },
    menuItem: {
        color: COLORS.LT_MIDNIGHT,
    },
});

function AttachmentsItem({
    classes,
    id,
    name,
    type,
    size,
    isUploading = false,
    onCancelUpload,
    isDelible = false,
    onDownload,
    onChangeLocation,
    onChangeDocumentType,
    onDelete,
    uploadProgress,
    source = 'LTOE',
    isReadOnly = false,
    isInlineEdition = false,
    locationId: initialLocationId,
    documentType: initialDocumentType,
    locations = [],
    readOnly = false,
}) {
    const { renderToast, showErrorToast } = useToast();
    const optionsRef = useRef(null);
    const [showOptions, setShowOptions] = useState(false);
    const [locationId, setLocationId] = useState(initialLocationId || '');
    const [documentType, setDocumentType] = useState(initialDocumentType || '');
    const [isProcessing, setIsProcessing] = useState(false);
    const toggleProcessing = () => setIsProcessing((prevValue) => !prevValue);

    const handleLocationChange = (event) => {
        toggleProcessing();
        onChangeLocation(
            { attachmentId: id, locationId: event.target.value },
            {
                onSuccess: ({ locationId: newLocationId }) => setLocationId(newLocationId || ''),
                onSettled: toggleProcessing,
            }
        );
    };

    const handleDocumentTypeChange = (event) => {
        toggleProcessing();
        onChangeDocumentType?.(
            { attachmentId: id, documentType: event.target.value },
            {
                onSuccess: ({ documentType: newDocumentType }) =>
                    setDocumentType(newDocumentType || ''),
                onSettled: toggleProcessing,
            }
        );
    };

    const handleOpen = async () => {
        if (isUploading) return;
        toggleProcessing();
        onDownload({
            onSuccess: async (data) => {
                try {
                    const blob = convertBase64ToBlob(data.image, data.type);
                    const fileURL = URL.createObjectURL(blob);
                    window.open(fileURL, '_blank');
                } catch (error) {
                    console.log('error', error);
                    showErrorToast('Failed to open attachment.');
                }
            },
            onSettled: toggleProcessing,
        });
    };

    const handleDownload = async () => {
        if (isUploading) return;
        toggleProcessing();
        onDownload({
            onSuccess: async (data) => {
                try {
                    const blob = convertBase64ToBlob(data.image, data.type);
                    downloadBlobFile({ fileName: name, blobData: blob });
                } catch (error) {
                    showErrorToast('Failed to download attachment.');
                }
            },
            onSettled: toggleProcessing,
        });
    };

    const handleDelete = () => {
        toggleProcessing();
        onDelete({ onSettled: toggleProcessing });
    };

    const handleShowOptions = () => {
        setShowOptions(true);
    };

    const handleHideOptions = () => {
        setShowOptions(false);
    };

    const hasMultipleLocations = locations?.length > 1;

    const selectedLocation = useMemo(
        () => locations?.find((location) => location.id === locationId)?.displayName,
        [locations, locationId]
    );
    const selectedDocumentType = useMemo(
        () => DOCUMENT_TYPES.find((item) => item.value === documentType)?.label,
        [documentType]
    );

    const canEdit = !isReadOnly;
    const canDelete = !isReadOnly && source === 'LTOE' && isDelible && onDelete;

    const renderLocationSelector = (props) => (
        <Select
            name="location"
            value={locationId}
            onChange={handleLocationChange}
            disabled={isProcessing}
            data-testid="location-select"
            sx={{
                height: 44,
                width: 'auto',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.LT_MIDNIGHT5,
                },
                '&.MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root .MuiInputBase-input': {
                    pr: 4,
                },
            }}
            displayEmpty={hasMultipleLocations || !locationId}
            {...props}
        >
            {(hasMultipleLocations || !locationId) && <MenuItem value="">All Locations</MenuItem>}
            {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                    {location.displayName}
                </MenuItem>
            ))}
        </Select>
    );

    const renderDocumentTypeSelector = () => (
        <Select
            name="documentType"
            value={documentType}
            onChange={handleDocumentTypeChange}
            disabled={isProcessing}
            data-testid="document-type-select"
            sx={{
                height: 44,
                width: 'auto',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.LT_MIDNIGHT5,
                },
                '&.MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root .MuiInputBase-input': {
                    pr: 4,
                },
            }}
            displayEmpty
        >
            {DOCUMENT_TYPES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                    {item.label}
                </MenuItem>
            ))}
            <MenuItem value="">Internal approval</MenuItem>
        </Select>
    );

    return (
        <>
            {renderToast({ vertical: 'bottom', horizontal: 'left' })}
            <Grid
                container
                className={classes.container}
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                gap={2}
                rowGap={1}
            >
                <Grid>
                    <Box className={classes.fileIcon} alignItems="center" justifyContent="center">
                        <DescriptionOutlinedIcon sx={{ color: 'white' }} />
                    </Box>
                </Grid>
                <Grid size="grow">
                    <Stack
                        direction="column"
                        alignItems="flex-start"
                        justifyContent="center"
                        alignSelf="stretch"
                        gap="5px"
                    >
                        <Button
                            type="button"
                            onClick={handleOpen}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View attachment"
                            disabled={isProcessing}
                            sx={{
                                textAlign: 'left',
                                padding: 0,
                                paddingRight: isUploading ? '12px' : 0,
                                justifyContent: 'flex-start',
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    color: COLORS.LT_MIDNIGHT,
                                    textDecoration: !isUploading ? 'underline' : 'none',
                                }}
                                fontSize="12px"
                                lineHeight="1.2"
                            >
                                {name}
                            </Typography>
                        </Button>
                        <Typography
                            sx={{ color: COLORS.LT_MIDNIGHT75 }}
                            fontSize="12px"
                            fontWeight="400"
                            lineHeight="1.2"
                        >
                            {type}&nbsp;&nbsp;|&nbsp;&nbsp;{size}
                        </Typography>
                    </Stack>
                </Grid>
                {isInlineEdition && (
                    <Grid size="grow">
                        {canEdit ? (
                            renderLocationSelector()
                        ) : (
                            <Typography
                                fontSize="12px"
                                fontWeight="400"
                                lineHeight="18px"
                                color={COLORS.LT_MIDNIGHT50}
                            >
                                {selectedLocation || 'All Locations'}
                            </Typography>
                        )}
                    </Grid>
                )}
                {!isUploading && (
                    <Grid position="relative">
                        {isProcessing && (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'absolute',
                                    right: isInlineEdition ? 35 : 20,
                                }}
                            >
                                <CircularProgress size={20} />
                            </Box>
                        )}
                        {isInlineEdition && canDelete && (
                            <IconButton
                                title="Delete"
                                onClick={handleDelete}
                                disabled={isProcessing}
                            >
                                <CancelIcon
                                    sx={{
                                        fontSize: 18,
                                        color: isProcessing
                                            ? COLORS.LT_MIDNIGHT5
                                            : COLORS.LT_MIDNIGHT25,
                                    }}
                                />
                            </IconButton>
                        )}
                        {!isInlineEdition && (
                            <Box alignSelf="flex-end">
                                <Button
                                    ref={optionsRef}
                                    onClick={handleShowOptions}
                                    disableRipple
                                    sx={{
                                        padding: 0,
                                        minWidth: 'auto',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: COLORS.LT_JOURNEY,
                                        },
                                        marginRight: '-15px',
                                    }}
                                    aria-label="Show options"
                                >
                                    <MoreVertOutlinedIcon
                                        fontSize="large"
                                        sx={{ color: COLORS.LT_MIDNIGHT25 }}
                                    />
                                </Button>
                            </Box>
                        )}
                    </Grid>
                )}
                {isUploading && (
                    <>
                        <IconButton
                            aria-label="Cancel upload"
                            sx={{ position: 'absolute', top: 0, right: 0 }}
                            onClick={onCancelUpload}
                        >
                            <CancelIcon fontSize="small" sx={{ color: COLORS.LT_JOURNEY }} />
                        </IconButton>
                        <Grid size={12}>
                            <LinearProgress value={uploadProgress ?? 0} variant="determinate" />
                        </Grid>
                    </>
                )}
                {!isUploading && !isInlineEdition && (
                    <Grid size={12}>
                        <FormControl fullWidth sx={{ mb: '10px' }}>
                            <InputLabel
                                id="location-select-label"
                                sx={{ fontSize: 14, fontWeight: 400, lineHeight: '22px' }}
                            >
                                Location
                            </InputLabel>
                            {canEdit ? (
                                renderLocationSelector({ labelId: 'location-select-label' })
                            ) : (
                                <Typography
                                    fontSize="14px"
                                    fontWeight="400"
                                    lineHeight="22px"
                                    color={COLORS.LT_MIDNIGHT50}
                                >
                                    {selectedLocation || 'All Locations'}
                                </Typography>
                            )}
                        </FormControl>
                        <FormControl fullWidth sx={{ mb: 0 }}>
                            <InputLabel
                                id="document-type-select-label"
                                sx={{ fontSize: 14, fontWeight: 400, lineHeight: '22px' }}
                            >
                                Document type
                            </InputLabel>

                            {canEdit && source === 'LTOE' ? (
                                renderDocumentTypeSelector({
                                    labelId: 'document-type-select-label',
                                })
                            ) : (
                                <Typography
                                    fontSize="14px"
                                    fontWeight="400"
                                    lineHeight="22px"
                                    color={COLORS.LT_MIDNIGHT50}
                                >
                                    {selectedDocumentType || 'Internal approval'}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                )}
            </Grid>
            <Menu
                anchorEl={optionsRef.current}
                open={showOptions}
                onClose={handleHideOptions}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                disableScrollLock
            >
                {canDelete && (
                    <MenuItem
                        onClick={handleDelete}
                        className={classes.menuItem}
                        disabled={isProcessing}
                    >
                        Delete
                    </MenuItem>
                )}
                <MenuItem
                    onClick={handleDownload}
                    className={classes.menuItem}
                    disabled={isProcessing}
                >
                    Download
                </MenuItem>
            </Menu>
        </>
    );
}

export default withStyles(styles)(AttachmentsItem);
